import React, { useCallback, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateChronologyFactWithSync } from '@/api/mutations/useCreateChronologyFactWithSync';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { ClassificationOptions, RelevanceOptions } from '@/constants/filterOptions';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Classification, Relevance } from '@/types';

interface AddChronologyFactDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddChronologyFactDialog({ isOpen, onOpenChange }: AddChronologyFactDialogProps) {
  const { matterId, subsetId, chronologyId } = useParams();
  if (!matterId || !subsetId || !chronologyId) return null;

  const [selectedDoc, setSelectedDoc] = useState<string>('');
  const [significance, setSignificance] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [factDateText, setFactDateText] = useState<string>('');
  const [selectedRelevance, setSelectedRelevance] = useState<Relevance>(Relevance.Relevant);
  const [selectedClassification, setSelectedClassification] = useState<Classification>(Classification.Primary);

  const { data: docIdsResponse } = useGetDocIds(matterId, subsetId);
  const { mutateAsync: createChronologyFact, isLoading: isLoadingAddNewFact } = useCreateChronologyFactWithSync(
    matterId,
    subsetId,
    chronologyId,
    selectedDoc,
  );

  const handleChangeDescription = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  const handleChangeSelectedDoc = useCallback((newValue: string) => {
    setSelectedDoc(newValue);
  }, []);

  const handleChangeDateOfSubjectMatterText = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFactDateText(e.target.value);
  }, []);

  const handleChangeSignificance = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSignificance(e.target.value);
  }, []);

  const docsOptions = useMemo(
    () => docIdsResponse?.documents?.map((doc) => ({ label: doc.file.name, value: doc.id })) ?? [],
    [docIdsResponse],
  );

  const handleAddFact = async () => {
    await createChronologyFact({
      description,
      factDateText,
      significance,
      classification: selectedClassification,
      relevance: selectedRelevance,
    });
    trackCustomEvent(TrackingEventsV2.AddFactToChronologyOnChronologyPage, { chronologyId, subsetId });
    onOpenChange(false);
  };

  const disabled = !selectedDoc || !description || !factDateText;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add Fact to Chronology</DialogTitle>
        </DialogHeader>
        {isLoadingAddNewFact ? (
          <div className="flex h-72 flex-col items-center justify-center gap-8">
            <div className="font-serif text-2xl">Adding Fact...</div>
            <Icon className="lucide--loader-circle h-6 w-6 animate-spin duration-700" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Fact Description</Label>
              <Input
                id="description"
                onChange={handleChangeDescription}
                value={description}
                placeholder="Enter fact description"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="document">Document</Label>
              <Select value={selectedDoc} onValueChange={handleChangeSelectedDoc}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a document" />
                </SelectTrigger>
                <SelectContent className="max-h-64 max-w-3xl overflow-y-auto">
                  {docsOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="factDate">Date of Fact</Label>
              <Input
                id="factDate"
                onChange={handleChangeDateOfSubjectMatterText}
                value={factDateText}
                placeholder="Enter date of fact"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="significance">
                Significance <i className="text-gray-500">(Optional)</i>
              </Label>
              <Input
                id="significance"
                onChange={handleChangeSignificance}
                value={significance}
                placeholder="Enter significance"
              />
            </div>

            <div className="flex gap-4">
              <div className="flex w-1/2 flex-col gap-2">
                <Label htmlFor="relevance">Relevance</Label>
                <Select
                  value={selectedRelevance}
                  onValueChange={(newValue) => setSelectedRelevance(newValue as Relevance)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select relevance" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 overflow-y-auto">
                    {Object.values(RelevanceOptions).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex w-1/2 flex-col gap-2">
                <Label htmlFor="classification">Classification</Label>
                <Select
                  value={selectedClassification}
                  onValueChange={(newValue) => setSelectedClassification(newValue as Classification)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select classification" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 overflow-y-auto">
                    {Object.values(ClassificationOptions).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-md bg-gray-50 p-3">
              <div className="flex-1">
                <p className="text-xs text-gray-600">
                  This will create a fact across the whole matter, meaning this fact will be visible in any other
                  subsets which include the document you are adding a fact to.
                </p>
              </div>
            </div>

            <Button onClick={handleAddFact} disabled={disabled}>
              <Icon className="lucide--plus-circle" />
              Add
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
